import { FC } from "react";

import { isQuickBooksAvailableForOrganization, Locales, QuickBooksMappingEntityType } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { FundFormData, FundFormFields } from "./FundFormFields";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { Amount } from "@/icons/outlined";

interface CreateFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFundCreated?: (fundId: string) => void;
}

export const CreateFundModal: FC<CreateFundModalProps> = ({ isOpen, onClose, onFundCreated }) => {
  const { t } = useTranslate();
  const { currentUser, organization } = useCurrentUserContext();
  const locale = currentUser?.locale ?? Locales.EN;

  const isQuickBooksAvailable = organization ? isQuickBooksAvailableForOrganization(organization) : false;

  const { data: qbConnection } = trpc.getQuickBooksConnection.useQuery(undefined, {
    enabled: isQuickBooksAvailable,
  });
  const hasQuickBooks = Boolean(qbConnection?.isConnected && qbConnection?.bankAccountId);

  const { data: qbMappingsData } = trpc.getQuickBooksMappings.useQuery(undefined, {
    enabled: hasQuickBooks,
  });

  const form = useForm<FundFormData>({
    defaultValues: {
      name: "",
      code: "",
      translatedName: "",
      qbAccountId: "",
      qbClassId: "",
      qbProductServiceId: "",
    },
  });

  const { errors } = form.formState;

  const { mutateAsync: createFund, isPending } = trpc.createFund.useMutation();
  const { mutateAsync: saveMapping } = trpc.saveQuickBooksMapping.useMutation();

  const qbIncomeAccounts = qbMappingsData?.incomeAccounts ?? [];
  const qbClasses = qbMappingsData?.classes ?? [];
  const qbProductsAndServices = qbMappingsData?.productsAndServices ?? [];

  const onSubmit = form.handleSubmit(async (data: FundFormData) => {
    const { data: createdFund, error } = await createFund({
      name: data.name,
      code: data.code || null,
      translatedName: data.translatedName || null,
    });
    if (error) {
      enqueueSnackbar(t("dashboard", "funds.create.error"), { vibe: "danger" });
    } else {
      if (createdFund?.id && data.qbAccountId) {
        await saveMapping({
          entityType: QuickBooksMappingEntityType.Fund,
          entityId: createdFund.id,
          qbAccountId: data.qbAccountId,
          qbClassId: data.qbClassId || null,
          qbProductServiceId: data.qbProductServiceId || null,
        });
      }
      enqueueSnackbar(t("dashboard", "funds.create.success"), { vibe: "positive" });
      if (createdFund?.id) {
        onFundCreated?.(createdFund.id);
      }
      onClose();
    }
  });

  return (
    <DashboardDialog open={isOpen} onClose={onClose} maxWidth="xs">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<Amount />} title={t("dashboard", "funds.create.title")} />,
          content: (
            <DashboardDialogContent>
              <form noValidate>
                <FundFormFields
                  control={form.control}
                  errors={errors}
                  locale={locale}
                  organizationCountry={organization?.country}
                  onTranslationRemoved={() => form.setValue("translatedName", "")}
                  hasQuickBooks={hasQuickBooks}
                  qbIncomeAccounts={qbIncomeAccounts}
                  qbClasses={qbClasses}
                  qbProductsAndServices={qbProductsAndServices}
                />
              </form>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={onSubmit} isLoading={isPending}>
                {t("dashboard", "funds.create.addFund")}
              </DashboardButton>
              <DashboardButton onClick={onClose}>{t("dashboard", "common.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
